/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import ConnectedPaginator from 'bundles/admin-shared/components/ConnectedPaginator';
import EmptyResultsRow from 'bundles/admin-shared/components/EmptyResultsRow';
import {
  SortablePaginatedTableContextProvider,
  useSortablePaginatedTableContext,
} from 'bundles/admin-shared/components/SortablePaginatedTableProvider';
import { TableMultiSelect } from 'bundles/admin-shared/components/TableMultiSelect';
import { Table, Tbody } from 'bundles/admin-shared/components/styledTableComponents';
import { compareByNaturalSortOrder } from 'bundles/author-common/utils/common';

import _t from 'i18n!nls/staff-management';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { staffTableRowKey } from '../StaffTable';
import type { StaffTableRow } from '../types';
import TableBulkActionsBar from './components/TableBulkActionsBar';
import TableHeader from './components/TableHeader';
import TableRow from './components/TableRow';
import { SORT_KEYS } from './constants';
import type { SortKeyRecordValue } from './constants';

interface Props {
  rows: StaffTableRow[];
  onBatchDelete: (rows: StaffTableRow[]) => void;
  onBatchEdit: (rows: StaffTableRow[]) => void;
}

const COLUMN_COUNT = 5;
const styles = {
  table: () => css`
    th,
    td {
      width: 26%;

      &:first-of-type,
      &:last-of-type {
        width: 11%;
      }
    }
  `,
};

const CDSLikeTableInner = ({ rows, onBatchDelete, onBatchEdit }: Props) => {
  const { sort, pagination } = useSortablePaginatedTableContext();
  const { totalRecords, setTotalRecords, page, pageSize } = pagination;

  // Enforce total records and row length equality
  React.useEffect(() => {
    if (rows.length !== totalRecords) {
      setTotalRecords(rows.length);
    }
  }, [rows.length, setTotalRecords, totalRecords]);

  const renderRows = React.useMemo(() => {
    const sortColumn = sort.key as SortKeyRecordValue;

    return [...rows].sort((rowA, rowB) => {
      const [firstRow, secondRow] = sort.direction === 'ASC' ? [rowA, rowB] : [rowB, rowA];

      const firstVal = firstRow[sortColumn];
      const secondVal = secondRow[sortColumn];

      return compareByNaturalSortOrder(firstVal, secondVal);
    });
  }, [rows, sort]);

  const zeroBasedPage = page - 1;
  const startIndex = zeroBasedPage * pageSize;
  const endIndex = startIndex + pageSize;
  const renderRowPage = renderRows.slice(startIndex, endIndex);

  return (
    <TableMultiSelect visibleRows={renderRowPage} keyFromRow={staffTableRowKey}>
      <TableBulkActionsBar rowsFromAllPages={rows} onBatchDelete={onBatchDelete} onBatchEdit={onBatchEdit} />
      <Grid container justifyContent="space-between" alignItems="center" xs={12} spacing={32}>
        <Grid item>
          <Table css={styles.table}>
            <TableHeader />
            <Tbody>
              {renderRowPage.map((r) => (
                <TableRow key={r.id} row={r} />
              ))}
            </Tbody>
            {renderRows.length === 0 && <EmptyResultsRow colSpan={COLUMN_COUNT}>{_t('No result')}</EmptyResultsRow>}
          </Table>
        </Grid>
      </Grid>
    </TableMultiSelect>
  );
};

const CDSLikeTable = (props: Props) => (
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  <SortablePaginatedTableContextProvider initialSortKey={SORT_KEYS.fullName}>
    <CDSLikeTableInner {...props} />
    <ConnectedPaginator />
  </SortablePaginatedTableContextProvider>
);

export default CDSLikeTable;
